import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaPlay, FaPause, FaHeart, FaMusic, FaGlobe } from "react-icons/fa";
import "../styles/MusicCard.css";

// Global audio manager to prevent multiple songs playing at once
const AudioManager = {
  currentlyPlaying: null,
  currentCardId: null,
  // List of all card state setters
  stateSetters: {},

  // Register a card's state setter
  registerCard: function (cardId, setIsPlayingFunc) {
    this.stateSetters[cardId] = setIsPlayingFunc;
  },

  // Unregister a card when it unmounts
  unregisterCard: function (cardId) {
    delete this.stateSetters[cardId];
  },

  // Update UI state for all cards
  updateAllCardStates: function () {
    // Set all cards to not playing except the current one
    Object.keys(this.stateSetters).forEach((cardId) => {
      const isPlaying = cardId === this.currentCardId;
      this.stateSetters[cardId](isPlaying);
    });
  },

  // Stop the currently playing audio
  stopCurrent: function () {
    if (this.currentlyPlaying) {
      this.currentlyPlaying.pause();
      this.currentlyPlaying.currentTime = 0;
      this.currentlyPlaying = null;
      this.currentCardId = null;
      this.updateAllCardStates();
    }
  },

  // Play a new audio
  play: function (cardId, audio) {
    // Stop any currently playing audio
    this.stopCurrent();

    // Set the new audio as current
    this.currentlyPlaying = audio;
    this.currentCardId = cardId;

    // Update all card states
    this.updateAllCardStates();

    return audio.play();
  },

  // Pause the current audio
  pause: function (cardId) {
    if (this.currentCardId === cardId && this.currentlyPlaying) {
      this.currentlyPlaying.pause();
      this.currentlyPlaying = null;
      this.currentCardId = null;
      this.updateAllCardStates();
    }
  },
};

const MusicCard = ({ song }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [audioUrl, setAudioUrl] = useState("");
  const [audio, setAudio] = useState(null);

  // Generate a unique ID for this card
  const cardId = song.id || `card-${Math.random().toString(36).substr(2, 9)}`;

  // Get unique sample audio for each artist
  useEffect(() => {
    try {
      // Use different audio tracks for each artist based on their ID
      // SoundHelix has tracks 1-9 available
      const audioIndex = song.id
        ? (parseInt(song.id.replace(/\D/g, "")) % 9) + 1
        : 1;
      const demoAudio = `https://www.soundhelix.com/examples/mp3/SoundHelix-Song-${audioIndex}.mp3`;

      setAudioUrl(demoAudio);

      // Create and preload the audio
      const newAudio = new Audio();
      newAudio.src = demoAudio;
      newAudio.preload = "auto";
      newAudio.addEventListener("canplaythrough", () => {});
      newAudio.addEventListener("error", (e) => {
        console.error(`Error loading audio for ${song.name}:`, e);
      });

      setAudio(newAudio);

      return () => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      };
    } catch (error) {
      console.error("Error setting up audio:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [song.id, song.name]);

  const togglePlay = () => {
    if (!audio) {
      console.error("No audio object available");
      return;
    }

    try {
      if (isPlaying) {
        AudioManager.pause(cardId);
        // No need to call setIsPlaying here as AudioManager will update all card states
      } else {
        // Use the improved AudioManager to play audio
        const playPromise = AudioManager.play(cardId, audio);

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // No need to call setIsPlaying here as AudioManager will update all card states
            })
            .catch((error) => {
              console.error("Error playing audio:", error);
              // Reset all states on error
              AudioManager.stopCurrent();
            });
        }
      }
    } catch (error) {
      console.error("Error in togglePlay:", error);
      // Reset all states on error
      AudioManager.stopCurrent();
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  // Register this card with the AudioManager
  useEffect(() => {
    AudioManager.registerCard(cardId, setIsPlaying);

    // Clean up when component unmounts
    return () => {
      AudioManager.unregisterCard(cardId);
    };
  }, [cardId]);

  // Add debugging for song data
  useEffect(() => {
    // Pre-load the fallback image to ensure it's in cache when needed
    const img = new Image();
    img.src = "https://via.placeholder.com/150?text=Music";
  }, [song]);

  // Extract location information - handle potential missing fields
  const location =
    song &&
    song.locations &&
    Array.isArray(song.locations) &&
    song.locations.length > 0
      ? song.locations[0]
      : null;
  const locationText =
    location && location.city && location.country
      ? `${location.city}, ${location.country}`
      : "";

  return (
    <motion.div
      className="music-card"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5 }}
    >
      <div className="album-cover">
        {/* Use a stable background with artist name overlay instead of text in image */}
        <div className="artist-image-container">
          <div
            className="artist-image-background"
            style={{
              backgroundColor: `#${Math.floor(
                Math.random() * 16777215
              ).toString(16)}80`, // Random color with 50% opacity
            }}
          ></div>
          {song.image ? (
            <img
              src={song.image}
              alt={song.name || "Artist"}
              className="artist-image"
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite error loop
                e.target.style.display = "none"; // Hide the image on error
              }}
            />
          ) : (
            <div className="artist-name-overlay">
              <FaMusic size={32} />
              <span>{song.name || "Artist"}</span>
            </div>
          )}
        </div>
        <motion.button
          className="play-btn"
          onClick={togglePlay}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          {isPlaying ? <FaPause /> : <FaPlay />}
        </motion.button>
      </div>
      <div className="song-info">
        <h3>{song.name}</h3>
        {song.website && (
          <a
            href={song.website}
            target="_blank"
            rel="noopener noreferrer"
            className="artist-website"
          >
            <FaGlobe /> Visit Website
          </a>
        )}
        {locationText && (
          <div className="location-info">
            <FaMusic /> Artist from {locationText}
          </div>
        )}
        <div className="song-meta">
          <span>Joined: {new Date(song.joindate).toLocaleDateString()}</span>
          <motion.button
            className={`favorite-btn ${isFavorite ? "active" : ""}`}
            onClick={toggleFavorite}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaHeart />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default MusicCard;
